import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';

interface Review {
  id: string;
  name: string;
  title?: string;
  facility: string;
  rating: number;
  body: string;
  date: string;
}

const VISIBLE = 3;

const Reviews: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [active, setActive] = useState(0);
  const [fading, setFading] = useState(false);
  const [paused, setPaused] = useState(false);
  const activeRef = useRef(0);

  useEffect(() => {
    fetch('/reviews/approved.json')
      .then(r => r.ok ? r.json() : [])
      .then((data: Review[]) => setReviews(data))
      .catch(() => setReviews([]));
  }, []);

  const total = Math.max(0, reviews.length - VISIBLE + 1);

  const goTo = (idx: number) => {
    if (idx === activeRef.current) return;
    setFading(true);
    setTimeout(() => {
      activeRef.current = idx;
      setActive(idx);
      setFading(false);
    }, 300);
  };

  useEffect(() => {
    if (total <= 1 || paused) return;
    const id = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        const next = (activeRef.current + 1) % total;
        activeRef.current = next;
        setActive(next);
        setFading(false);
      }, 300);
    }, 5000);
    return () => clearInterval(id);
  }, [total, paused]);

  const visible = reviews.slice(active, active + VISIBLE);

  return (
    <section style={{ background: 'linear-gradient(135deg, var(--color-navy) 0%, var(--color-primary) 100%)', padding: '5rem 2rem' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <h2 style={{ textAlign: 'center', color: 'var(--color-white)', fontSize: '2rem', marginBottom: '0.5rem' }}>What Our Customers Say</h2>
        <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.75)', marginBottom: '3rem' }}>
          Real feedback from healthcare professionals we've worked with.
        </p>

        {reviews.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
            <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '1.1rem', marginBottom: '1.5rem' }}>No reviews yet — be the first to share your experience!</p>
            <Link to="/reviews" style={{ background: 'white', color: 'var(--color-primary)', padding: '0.75rem 2rem', borderRadius: '50px', fontWeight: 600, textDecoration: 'none', fontSize: '0.95rem' }}>
              Leave a Review →
            </Link>
          </div>
        ) : (
          <>
            <div
              className="ow-reviews-grid"
              role="region"
              aria-label="Client reviews"
              aria-live="polite"
              aria-atomic="true"
              style={{ opacity: fading ? 0 : 1, transition: 'opacity 0.3s ease' }}
            >
              {visible.map(r => (
                <div key={r.id} className="ow-review-card" style={{ background: 'rgba(255,255,255,0.1)', borderTop: '4px solid rgba(255,255,255,0.3)', boxShadow: 'none' }}>
                  <div aria-label={`${r.rating} out of 5 stars`} style={{ fontSize: '1.1rem', marginBottom: '0.75rem' }}>
                    <span aria-hidden="true">{'⭐'.repeat(r.rating)}</span>
                  </div>
                  <p style={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.7, marginBottom: '1.25rem', fontStyle: 'italic' }}>"{r.body}"</p>
                  <div>
                    <strong style={{ color: 'var(--color-white)', display: 'block' }}>{r.name}</strong>
                    {r.title && <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.875rem' }}>{r.title}</span>}
                    {r.title && <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.875rem' }}> · </span>}
                    <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.875rem' }}>{r.facility}</span>
                  </div>
                </div>
              ))}
            </div>

            {total > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '2rem' }}>
                {Array.from({ length: total }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goTo(i)}
                    aria-label={`Go to review ${i + 1}`}
                    aria-current={i === active ? 'true' : undefined}
                    style={{
                      width: i === active ? '24px' : '10px',
                      height: '10px',
                      borderRadius: '5px',
                      border: 'none',
                      background: i === active ? 'var(--color-primary)' : 'var(--color-muted-on-dark-light)',
                      cursor: 'pointer',
                      padding: 0,
                      transition: 'all 0.3s ease',
                    }}
                  />
                ))}
                <button
                  onClick={() => setPaused(p => !p)}
                  aria-label={paused ? 'Play reviews carousel' : 'Pause reviews carousel'}
                  style={{ marginLeft: '0.5rem', background: 'none', border: '1px solid var(--color-muted-on-dark-light)', borderRadius: '4px', cursor: 'pointer', padding: '0.2rem 0.5rem', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}
                >
                  {paused ? '▶' : '⏸'}
                </button>
              </div>
            )}

            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <Link to="/reviews" style={{ color: 'rgba(255,255,255,0.8)', fontWeight: 600, textDecoration: 'none', fontSize: '0.95rem' }}>
                Leave a Review →
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Reviews;
